import React from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Stack,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PlaceIcon from '@mui/icons-material/Place';
import { CmsProject, CmsConfiguration, ProjectStatus } from '../../types/cms';

const GOLD = '#9d6f32';
const BORDER = '#e8e8e8';

interface ProjectOverviewTabProps {
  project: CmsProject;
  onChange: (updated: CmsProject) => void;
}

export const ProjectOverviewTab: React.FC<ProjectOverviewTabProps> = ({ project, onChange }) => {
  const updateField = (field: keyof CmsProject, value: any) => {
    onChange({ ...project, [field]: value });
  };

  const handleConfigChange = (index: number, field: keyof CmsConfiguration, val: any) => {
    const nextConfigs = [...project.configurations];
    nextConfigs[index] = { ...nextConfigs[index], [field]: val };
    updateField('configurations', nextConfigs);
  };

  const handleAddConfig = () => {
    const newConfig: CmsConfiguration = {
      id: `c_${Date.now()}`,
      type: '2 BHK Premium',
      area: '1,150 sq.ft',
      carpetArea: '750 sq.ft',
      price: '₹45 – ₹50 Lakhs',
      units: 20,
      availabilityStatus: 'Available',
    };
    updateField('configurations', [...project.configurations, newConfig]);
  };

  const handleRemoveConfig = (index: number) => {
    const nextConfigs = project.configurations.filter((_, i) => i !== index);
    updateField('configurations', nextConfigs);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3.5 }}>
      {/* 1. Basic Project Identity */}
      <Paper sx={{ p: 3, borderRadius: 2, border: `1px solid ${BORDER}`, boxShadow: 'none' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5, color: '#1a1a1a' }}>
          🏢 Basic Project Identity & Locations
        </Typography>
        <Typography variant="body2" sx={{ color: '#6b6b6b', mb: 2.5 }}>
          Controls the primary headings, micro-market tags, and compliance details on the live website.
        </Typography>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2.5 }}>
          <TextField
            label="Project Name"
            fullWidth
            size="small"
            value={project.name}
            onChange={(e) => updateField('name', e.target.value)}
          />

          <TextField
            label="Tagline"
            fullWidth
            size="small"
            value={project.tagline}
            onChange={(e) => updateField('tagline', e.target.value)}
          />

          <Box sx={{ p: 2, bgcolor: '#fcfaf8', borderRadius: 2, border: `1px solid ${BORDER}` }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <PlaceIcon sx={{ color: GOLD, fontSize: 20 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
                Primary Micro-Market & Surrounding Target Localities
              </Typography>
            </Box>
            
            <TextField
              label="Primary Area / Main Micro-Market"
              fullWidth
              size="small"
              value={project.microMarket || 'Vanakla'}
              onChange={(e) => updateField('microMarket', e.target.value)}
              helperText="Main location anchor (e.g. Vanakla)"
              sx={{ mb: 2, bgcolor: '#ffffff' }}
            />

            <Typography variant="body2" sx={{ fontWeight: 600, color: '#555', mb: 1 }}>
              Surrounding Target Areas (used in SEO, search queries & landing hubs):
            </Typography>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
              {(project.surroundingAreas || ['Jahangirpura', 'Jahangirabad', 'Palanpore Canal Road', 'Dandi Road', 'Ugat Canal Road']).map((area) => (
                <Chip
                  key={area}
                  label={area}
                  size="small"
                  onDelete={() => {
                    const current = project.surroundingAreas || ['Jahangirpura', 'Jahangirabad', 'Palanpore Canal Road', 'Dandi Road', 'Ugat Canal Road'];
                    updateField('surroundingAreas', current.filter((a) => a !== area));
                  }}
                  sx={{ bgcolor: '#fef3c7', color: '#92400e', fontWeight: 600, border: '1px solid #fde68a' }}
                />
              ))}
            </Box>

            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                placeholder="Type area (e.g. Jahangirpura) and press Enter"
                size="small"
                sx={{ bgcolor: '#ffffff', flexGrow: 1 }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    const val = (e.target as HTMLInputElement).value.trim();
                    if (val) {
                      const current = project.surroundingAreas || ['Jahangirpura', 'Jahangirabad', 'Palanpore Canal Road', 'Dandi Road', 'Ugat Canal Road'];
                      if (!current.includes(val)) {
                        updateField('surroundingAreas', [...current, val]);
                      }
                      (e.target as HTMLInputElement).value = '';
                    }
                  }
                }}
              />
            </Stack>
          </Box>

          <TextField
            label="Full Address / Location"
            fullWidth
            size="small"
            value={project.location}
            onChange={(e) => updateField('location', e.target.value)}
          />

          <Stack direction="row" gap={1.5}>
            <TextField
              label="Site latitude"
              size="small"
              fullWidth
              value={project.latitude ?? ''}
              placeholder="21.2283"
              onChange={(e) => {
                const raw = e.target.value.trim();
                // Accept a pasted "lat, lng" pair from Google Maps.
                const pair = raw.match(/^(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)$/);
                if (pair) {
                  onChange({ ...project, latitude: Number(pair[1]), longitude: Number(pair[2]) });
                  return;
                }
                if (raw === '' || /^-?\d*\.?\d*$/.test(raw)) updateField('latitude', raw || null);
              }}
              helperText="Paste 'lat, lng' from Google Maps (right-click the site)"
            />
            <TextField
              label="Site longitude"
              size="small"
              fullWidth
              value={project.longitude ?? ''}
              placeholder="72.7801"
              onChange={(e) => {
                const raw = e.target.value.trim();
                if (raw === '' || /^-?\d*\.?\d*$/.test(raw)) updateField('longitude', raw || null);
              }}
              helperText="Sent as the WhatsApp location pin"
            />
          </Stack>

          <FormControl size="small" fullWidth>
            <InputLabel>Construction / Possession Status</InputLabel>
            <Select
              label="Construction / Possession Status"
              value={project.status}
              onChange={(e) => updateField('status', e.target.value as ProjectStatus)}
            >
              <MenuItem value="under-construction">Under Construction (Possession: Dec 2026)</MenuItem>
              <MenuItem value="ready-possession">Ready Possession (BUC Received - 0% GST)</MenuItem>
              <MenuItem value="upcoming">Upcoming Launch</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Target Possession Timeline"
            fullWidth
            size="small"
            value={project.completionDate}
            onChange={(e) => updateField('completionDate', e.target.value)}
            helperText="e.g. December 2026 or Ready Possession (OC Received)"
          />

          <TextField
            label="RERA Registration Number"
            fullWidth
            size="small"
            value={project.reraNumber}
            onChange={(e) => updateField('reraNumber', e.target.value)}
          />

          <TextField
            label="Sales WhatsApp Number"
            fullWidth
            size="small"
            value={project.whatsappNumber}
            onChange={(e) => updateField('whatsappNumber', e.target.value)}
            helperText="Include country code, e.g. +919876543210"
          />
        </Box>

        <Box sx={{ mt: 2.5, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2.5 }}>
          <TextField
            label="Total Floors"
            type="number"
            size="small"
            value={project.totalFloors}
            onChange={(e) => updateField('totalFloors', Number(e.target.value))}
          />

          <TextField
            label="Total Units"
            type="number"
            size="small"
            value={project.totalUnits}
            onChange={(e) => updateField('totalUnits', Number(e.target.value))}
          />
        </Box>

        <Box sx={{ mt: 2.5 }}>
          <TextField
            label="Short Description (Appears on Card Views & Summaries)"
            fullWidth
            multiline
            rows={2}
            size="small"
            value={project.shortDescription}
            onChange={(e) => updateField('shortDescription', e.target.value)}
          />
        </Box>

        <Box sx={{ mt: 2.5 }}>
          <TextField
            label="Detailed Project Story & Description"
            fullWidth
            multiline
            rows={4}
            size="small"
            value={project.description}
            onChange={(e) => updateField('description', e.target.value)}
          />
        </Box>
      </Paper>

      {/* 2. Typologies, Configurations & Live Pricing Table */}
      <Paper sx={{ p: 3, borderRadius: 2, border: `1px solid ${BORDER}`, boxShadow: 'none' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
              📐 Typologies, Carpet Area & Pricing
            </Typography>
            <Typography variant="body2" sx={{ color: '#6b6b6b' }}>
              These details appear in project content after publication. Check the public page and any structured data separately.
            </Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAddConfig}
            sx={{
              borderColor: GOLD,
              color: GOLD,
              textTransform: 'none',
              fontWeight: 700,
              '&:hover': { bgcolor: 'rgba(157,111,50,0.06)' },
            }}
          >
            Add Configuration
          </Button>
        </Box>

        <TableContainer component={Box} sx={{ border: `1px solid ${BORDER}`, borderRadius: 1.5 }}>
          <Table size="small">
            <TableHead sx={{ bgcolor: '#f9fafb' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Typology</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Super Built-Up Area</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Carpet Area</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Price Range</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Units</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Availability</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, fontSize: 12 }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {project.configurations.map((c, idx) => (
                <TableRow key={c.id || idx}>
                  <TableCell>
                    <TextField
                      size="small"
                      value={c.type}
                      onChange={(e) => handleConfigChange(idx, 'type', e.target.value)}
                      placeholder="e.g. 2 BHK Luxury"
                      sx={{ minWidth: 140 }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      value={c.area}
                      onChange={(e) => handleConfigChange(idx, 'area', e.target.value)}
                      placeholder="1,280 sq.ft"
                      sx={{ minWidth: 120 }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      value={c.carpetArea || ''}
                      onChange={(e) => handleConfigChange(idx, 'carpetArea', e.target.value)}
                      placeholder="820 sq.ft"
                      sx={{ minWidth: 110 }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      value={c.price}
                      onChange={(e) => handleConfigChange(idx, 'price', e.target.value)}
                      placeholder="₹48 – ₹54 Lakhs"
                      sx={{ minWidth: 130 }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      type="number"
                      value={c.units || ''}
                      onChange={(e) => handleConfigChange(idx, 'units', Number(e.target.value))}
                      sx={{ width: 80 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Select
                      size="small"
                      value={c.availabilityStatus || 'Available'}
                      onChange={(e) => handleConfigChange(idx, 'availabilityStatus', e.target.value)}
                      sx={{ minWidth: 130 }}
                    >
                      <MenuItem value="Available">Available</MenuItem>
                      <MenuItem value="Booking Fast">Booking Fast</MenuItem>
                      <MenuItem value="Limited Units">Limited Units</MenuItem>
                      <MenuItem value="Sold Out">Sold Out</MenuItem>
                    </Select>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveConfig(idx)}
                      disabled={project.configurations.length <= 1}
                      sx={{ color: '#ef4444' }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};
